import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import InsuranceCredentialsDetail from 'components/InsuranceCredentialsManager/InsuranceCredentialsDetail';
import InsuranceCredentialsList from 'components/InsuranceCredentialsManager/InsuranceCredentialsList';
import LoadingOverlay from 'components/UI/LoadingOverlay';
import { type FC, useCallback, useEffect, useState } from 'react';
import {
  useGetBusinessUnitInsuranceCredentialsMutation,
  useUpdateBusinessUnitInsuranceCredentialsMutation,
} from 'services/api/businessUnits.api';
import type { InsuranceBrokerCredential, InsuranceCompany } from 'services/types/businessUnit';
import { RADIUS_CARD, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface Props {
  businessUnitId: number;
  onRefetch: () => void;
}

/**
 * Component for managing insurance credentials for a specific business unit
 *
 * @param businessUnitId - ID of the business unit
 * @param onRefetch - Callback to trigger refetch
 * @returns React component for managing insurance credentials for a business unit
 */
const InsuranceCredentialsForBusinessUnit: FC<Props> = ({ businessUnitId, onRefetch }) => {
  const [updateCredentials, { isLoading: isUpdatingCredentials }] = useUpdateBusinessUnitInsuranceCredentialsMutation();
  const [getCredentials, { isLoading: isLoadingCredentials }] = useGetBusinessUnitInsuranceCredentialsMutation();
  const [credentials, setCredentials] = useState<InsuranceBrokerCredential[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<InsuranceCompany | null>(null);
  const [credentialsLoaded, setCredentialsLoaded] = useState(false);

  /**
   * Loads existing credentials for the business unit
   */
  const loadCredentials = useCallback(async () => {
    if (!businessUnitId) return;

    try {
      const response = await getCredentials(businessUnitId).unwrap();
      setCredentials(response.insuranceBrokerCredentials || []);
      setCredentialsLoaded(true);
    } catch (_error) {
      setCredentialsLoaded(true);
    }
  }, [businessUnitId, getCredentials]);

  /**
   * Load credentials when businessUnitId changes
   */
  useEffect(() => {
    if (businessUnitId) {
      void loadCredentials();
    }
  }, [businessUnitId, loadCredentials]);

  /**
   * Handles credential changes
   *
   * @param newCredentials - Updated credentials array
   */
  const handleCredentialsChange = (newCredentials: InsuranceBrokerCredential[]) => {
    setCredentials(newCredentials);
  };

  /**
   * Saves the insurance credentials to the business unit
   */
  const handleSaveCredentials = async (credentialsToSave?: InsuranceBrokerCredential[]) => {
    if (!businessUnitId) {
      return;
    }

    // Use the passed credentials or fall back to current state
    const credentialsToUse = credentialsToSave || credentials;

    try {
      const response = await updateCredentials({
        businessUnitId,
        insuranceBrokerCredentials: credentialsToUse,
      }).unwrap();

      // Update credentials with the response from server (with masked passwords)
      setCredentials(response.insuranceBrokerCredentials || []);
      onRefetch(); // Trigger refetch to update the parent component
    } catch (_saveError) {
      alert('Error al guardar las credenciales. Por favor, inténtalo de nuevo.');
    }
  };

  if (!credentialsLoaded) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <LoadingOverlay visible={isUpdatingCredentials || isLoadingCredentials} />
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ height: { xs: 'auto', md: '70vh' }, mt: { xs: 2, md: 0 } }}>
        {/* Left Panel - Company List */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              height: { xs: 'auto', md: '100%' },
              maxHeight: { xs: '56px', md: 'none' },
              overflow: { xs: 'visible', md: 'hidden' },
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: { xs: RADIUS_CARD, md: RADIUS_COIN },
              boxShadow: SHADOW,
            }}
          >
            <InsuranceCredentialsList
              credentials={credentials}
              selectedCompany={selectedCompany}
              onCompanySelect={setSelectedCompany}
            />
          </Box>
        </Grid>

        {/* Right Panel - Credential Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box
            sx={{
              height: { xs: 'auto', md: '100%' },
              overflow: 'hidden',
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              borderRadius: { xs: RADIUS_CARD, md: RADIUS_COIN },
              boxShadow: SHADOW,
            }}
          >
            <InsuranceCredentialsDetail
              selectedCompany={selectedCompany}
              credentials={credentials}
              onCredentialsChange={handleCredentialsChange}
              onSave={handleSaveCredentials}
              isSaving={isUpdatingCredentials}
              businessUnitId={businessUnitId}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default InsuranceCredentialsForBusinessUnit;
